import React, { useState, useMemo } from 'react';
import classNames from 'classnames';
import { Button } from '@tencent/spaui';
import { ImageViewer } from '@tencent/ptp-fe-common';

export interface FormItem {
  name: string;
  label: string;
  display?: (data: any) => boolean;
  render?: (data: any) => any;
  type?: 'image' | 'file' | 'link' | 'normal' | 'textArea';
}

interface FormProps {
  title: string;
  data: any;
  mode: 'text' | 'edit';
  config?: Array<FormItem>;
  EditComponent?: React.FC;
  editProps?: Record<string, any>;
  onSubmit?: (editClose: () => void) => void;
}

const showFileTypeReg = /\.(img|png|jpeg|jpg|gif|bmp|wmv|asf|mp4|mov|rm|rmvb)/i;

const FileDownload = (fileUrl: string, fileName = '点击下载查看') => {
  if (!fileUrl) {
    return null;
  }
  if (typeof fileUrl !== 'string') {
    return null;
  }
  return (
    <span
      style={{
        cursor: 'pointer',
        color: '#296bef',
        height: '36px',
        lineHeight: '36px',
        marginRight: '12px',
        display: 'block',
      }}
      onClick={() => {
        const reg = /^http(s)?:/;
        const link = fileUrl.replace(reg, '');
        window.open(`${location.protocol}${link}`);
      }}
    >
      {fileName}
    </span>
  );
};

const useForm = (props: FormProps) => {
  const { title, config, data = {}, mode, EditComponent, editProps, onSubmit } = props;
  const [isEditing, setIsEditing] = useState(false);

  const editClose = () => {
    setIsEditing(false);
  };

  const FormComponent = useMemo(
    () => (
      <div className="page-form-group">
        <div className="form-group-header">
          <h4>{title}</h4>
          {mode === 'text' && !isEditing && !!EditComponent && (
            <Button displayType="link" onClick={() => setIsEditing(true)}>
              编辑信息
            </Button>
          )}
        </div>
        {isEditing ? (
          !!EditComponent && (
            <>
              <EditComponent {...editProps} />
              <div className="form-group-footer">
                <Button displayType="primary" onClick={() => onSubmit(editClose)}>
                  确定
                </Button>
                <Button onClick={editClose}>取消</Button>
              </div>
            </>
          )
        ) : (
          <div className="form-group-content">
            {config.map(({ name, label, render, type, display }) => {
              if (typeof display === 'function' && !display(data)) {
                return null;
              }
              let value = data[name] || '';
              if (typeof render === 'function') {
                value = render(data);
              } else if (type === 'link') {
                value = (
                  <a target="_blank" href={value} rel="noreferrer">
                    {value}
                  </a>
                );
              } else if (type === 'image' && value) {
                const files = Array.isArray(value) ? value : value.split(',');
                value = files.map((v, index) => {
                  const url = v.image_url || v;
                  const res = url?.match?.(showFileTypeReg);
                  if (res) {
                    return (
                      <ImageViewer
                        type="images"
                        key={index}
                        showThumbnail={true}
                        showThumbnailTitle={false}
                        usePlace={false}
                        images={[
                          {
                            url,
                          },
                        ]}
                      />
                    );
                  }
                  return FileDownload(url);
                });
              } else if (type === 'file' && value) {
                const files = Array.isArray(value) ? value : [value];
                value = files.map(({ file_url, file_name }, index) => {
                  const res = file_name.match(showFileTypeReg);
                  if (res) {
                    return (
                      <ImageViewer
                        type="images"
                        key={index}
                        images={[
                          {
                            name: file_name || file_url,
                            url: file_url,
                          },
                        ]}
                        placeholder={file_name || file_url}
                      />
                    );
                  }
                  return FileDownload(file_url, file_name);
                });
              } else if (type === 'textArea' && value) {
                value = value.split('\n').map((item: string, index: number) => <div key={index}>{item}</div>);
              }
              if (!value || (Array.isArray(value) && !value.length)) {
                value = '-';
              }
              return (
                <div className={classNames('form-item', { 'align-top': ['image', 'file'].includes(type) })} key={name}>
                  <div className="form-item-label">{label}</div>
                  <div className={classNames('form-item-component', { row: type === 'image' })}>{value}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    ),
    [title, config, data, EditComponent, editProps, isEditing, mode, onSubmit]
  );

  return {
    Form: FormComponent,
  };
};

export default useForm;
